// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { ErrorInformationStorage } from '@shared/commons/ErrorInformationStorage';
import { TerceraChartBaseRenderer } from '../TerceraChartBaseRenderer';
import { SelectionState } from '../../Tools/Selection';
import { ToolViewFactory } from '../../Tools/Utils/ToolViewFactory';
import { SelectionModeEnum } from '@shared/commons/cache/DataCacheToolEnums';
import { DataCacheToolFactory } from '@shared/commons/cache/DataCacheToolFactory';
import { ToolsCache } from '@shared/commons/cache/ToolsCache';
import { MouseButtons } from '@front/controls/UtilsClasses/ControlsUtils';
import { TerceraChartAction, TerceraChartActionEnum } from '../../TerceraChartAction';
import { TerceraChartUtils } from '../../TerceraChartUtils';
import { ScreenPointsConverterFactory } from '../../Utils/IScreenPointsConverter';
import { CustomEvent } from '@shared/utils/CustomEvents';
import { ActionType } from '../../Tools/ToolView';
import { LayersEnum } from '../../Utils/LayersEnum';
export class TerceraChartToolRenderer extends TerceraChartBaseRenderer {
    constructor(chartID, chart, newToolRenderer) {
        super(chart);
        this.WindowsNumber = 0;
        this.SupportDataConverter = true;
        this.chartID = chartID;
        this.newToolRenderer = newToolRenderer;
        this.screenPoinsConverter = null;
        this.SelectedToolsListChanged = new CustomEvent();
        this.tools = [];
        this.hoverTool = null;
        this.movingTools = [];
        this.instrument = null;
        this.ApplyNewSnapMode(false);
        this.Subscribe();
        this.ToolHovered = new CustomEvent();
        this.assignLayer = LayersEnum.Tools;
        this.SetClassName('TerceraChartToolRenderer');
    }
    get Instrument() {
        return this.instrument;
    }
    set Instrument(value) {
        this.instrument = value;
        this.RepopulateTools();
    }
    HoverToolSet(value) {
        if (this.hoverTool == value) {
            return;
        }
        this.hoverTool = value;
        this.OnToolHovered();
    }
    HoverToolGet() {
        return this.hoverTool;
    }
    OnToolHovered() {
        this.ToolHovered.Raise(this.hoverTool);
    }
    Subscribe() {
        ToolsCache.OnAddTool.Subscribe(this.ToolsCache_OnAddTool, this);
        ToolsCache.OnRemoveTool.Subscribe(this.ToolsCache_OnRemoveTool, this);
    }
    ToolsCache_OnAddTool(tool, e) {
        if (this.instrument == null) {
            return;
        }
        if (tool.WindowsNumber !== this.WindowsNumber)
            return;
        if (ToolsCache.CheckToolEnabilityAndUpdateInstrument(tool, this.instrument)) {
            const toolview = ToolViewFactory.CreateToolView(tool, this.chartID, false);
            this.tools.push(toolview);
            toolview.OnFinishCreationTool(e);
        }
    }
    ToolsCache_OnRemoveTool(tool) {
        if (this.instrument == null) {
            return;
        }
        const toRemove = this.GetToolViewByDataCacheTool(tool);
        if (toRemove != null) {
            const remIndex = this.tools.indexOf(toRemove);
            this.tools.splice(remIndex, 1);
            if (this.hoverTool === toRemove) {
                this.HoverToolSet(null);
            }
            if (toRemove.CurrentSelection.CurrentState == SelectionState.Selected) {
                this.OnSelectedToolsListChanged();
            }
            toRemove.Dispose();
        }
    }
    GetToolViewByDataCacheTool(tool) {
        for (let i = 0; i < this.tools.length; i++) {
            if (this.tools[i].dataCacheTool === tool) {
                return this.tools[i];
            }
        }
        return null;
    }
    RemoveSelectedAndHoverTools() {
        const tools = this.tools;
        let i = tools.length;
        while (i--) {
            const tool = tools[i];
            if (tool && tool.CurrentSelection.CurrentState !== SelectionState.None) {
                this.RemoveTool(tool);
            }
        }
    }
    RemoveToolsByType(type) {
        const tools = this.tools;
        let i = tools.length;
        while (i--) {
            const tool = tools[i];
            if (tool && tool.dataCacheTool.ToolType === type) {
                this.RemoveTool(tool);
            }
        }
    }
    RemoveAllTools() {
        const tools = this.tools;
        let i = tools.length;
        while (i--)
            this.RemoveTool(tools[i]);
    }
    RepopulateTools() {
        const filteredTools = ToolsCache.GetTools(this.instrument);
        const newToolsView = [];
        for (let i = 0; i < filteredTools.length; i++) {
            if (filteredTools[i].WindowsNumber !== this.WindowsNumber)
                continue;
            newToolsView.push(ToolViewFactory.CreateToolView(filteredTools[i], this.chartID, true));
        }
        this.tools = newToolsView;
        this.OnSelectedToolsListChanged();
    }
    Unsubscribe() {
        ToolsCache.OnAddTool.UnSubscribe(this.ToolsCache_OnAddTool, this);
        ToolsCache.OnRemoveTool.UnSubscribe(this.ToolsCache_OnRemoveTool, this);
    }
    Draw(gr, window, windowsContainer, advParams = null) {
        const param = advParams;
        const cashItemSeries = param.mainPriceRenderer.Series;
        const clientRect = window.ClientRectangle;
        gr.save();
        gr.beginPath();
        gr.rect(clientRect.X, clientRect.Y, clientRect.Width, clientRect.Height);
        gr.clip();
        this.DrawAllTools(gr, window, param);
        gr.restore();
    }
    DrawAllTools(gr, window, param) {
        const cashItemSeries = param.TerceraChart.mainPriceRenderer.Series;
        for (let i = 0; i < this.tools.length; i++) {
            const tool = this.tools[i];
            const stickToPeriod = tool.dataCacheTool.StickToPeriod;
            if (!(stickToPeriod === null || stickToPeriod === void 0 ? void 0 : stickToPeriod.length) || stickToPeriod.includes(cashItemSeries.CashItem.FPeriod)) {
                tool.UpdateScreenPoints(window, cashItemSeries);
                tool.Draw(gr, window, param);
            }
        }
    }
    Dispose() {
        this.Unsubscribe();
        this.chart = null;
        super.Dispose();
    }
    static CheckValidRange(window, x, y) {
        if (window === null) {
            return false;
        }
        const rect = window.ClientRectangle;
        return x >= rect.X && x < rect.X + rect.Width && y >= rect.Y && y < rect.Y + rect.Height;
    }
    CheckValidRange(e, x, y) {
        if (!e)
            return false;
        const window = e.window;
        if (!window)
            return false;
        const instanceWindow = this.InstanceWindow;
        if (instanceWindow && window !== instanceWindow) {
            return false;
        }
        return TerceraChartToolRenderer.CheckValidRange(window, x, y);
    }
    OnSelectedToolsListChanged() {
        this.SelectedToolsListChanged.Raise();
    }
    GetHoverAndSelectedToolsList() {
        const selectedToolsList = [];
        const tools = this.tools;
        const len = tools.length;
        for (let i = 0; i < len; i++) {
            const tv = tools[i];
            if (tv && tv.CurrentSelection.CurrentState !== SelectionState.None) {
                selectedToolsList.push(tv);
            }
        }
        return selectedToolsList;
    }
    ApplyNewSnapMode(useSnap) {
        this.screenPoinsConverter = ScreenPointsConverterFactory.GetScreenPointsConverter(useSnap);
    }
    IsMyTool(tool) {
        return this.tools.includes(tool);
    }
    CloneTool(tool, ww) {
        if (!ww)
            return;
        const clonedDCTool = DataCacheToolFactory.Clone(tool.dataCacheTool);
        TerceraChartUtils.ShiftTool(clonedDCTool, ww);
        ToolsCache.AddTool(clonedDCTool);
    }
    IsNewToolRendererActive(e) {
        var _a;
        return (_a = this.newToolRenderer) === null || _a === void 0 ? void 0 : _a.CanProcessAction(e.window, e.Location);
    }
    ProcessMouseMove(e) {
        try {
            if (this.IsNewToolRendererActive(e)) {
                return false;
            }
            const x = e.Location.X;
            const y = e.Location.Y;
            if (this.movingTools.length > 0) {
                if (this.CheckValidRange(e, x, y)) {
                    this.ProcessMovement(e, x, y);
                }
            }
            else {
                this.ProcessHoverTool(e, x, y);
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
        return false;
    }
    ProcessMouseUp(e) {
        let result = false;
        try {
            if (this.IsNewToolRendererActive(e)) {
                return false;
            }
            if (e.Button == MouseButtons.Left) {
                result = this.ProcessLeftButtonUp(e);
            }
        }
        catch (ex) {
            ErrorInformationStorage.GetException(ex);
            console.log(ex);
        }
        return result;
    }
    ProcessMouseDown(e) {
        if (this.IsNewToolRendererActive(e)) {
            return false;
        }
        if (e.Button == MouseButtons.Left) {
            try {
                const x = e.Location.X;
                const y = e.Location.Y;
                this.ProcessSelectionList(x, y);
                for (let i = 0; i < this.tools.length; i++) {
                    const tool = this.tools[i];
                    if (tool.ProcessActivate(x, y) &&
                        (tool.MainOrderVisibility ||
                            !isNullOrUndefined(tool.linkedOrderToolTipRect) ||
                            !tool.isTradingTool)) {
                        this.movingTools.push(tool);
                        return true;
                    }
                }
            }
            catch (ex) {
                ErrorInformationStorage.GetException(ex);
                console.log(ex);
            }
        }
        else if (e.Button == MouseButtons.Right && this.movingTools.length > 0) {
            try {
                for (let i = 0; i < this.movingTools.length; i++) {
                    const tradeTool = this.movingTools[i];
                    tradeTool.CancelMoving();
                    tradeTool.Updated(tradeTool.Order);
                    tradeTool.ProcessDeactivate();
                    const remIndex = this.movingTools.indexOf(tradeTool);
                    this.movingTools.splice(remIndex, 1);
                }
            }
            catch (ex) {
                ErrorInformationStorage.GetException(ex);
            }
            return true;
        }
        return false;
    }
    ProcessMouseLeave(e) {
        this.ProcessHoverTool(e, -1, -1);
    }
    ProcessSelectionList(x, y) {
        let raiseEvent = false;
        const controlPressed = false;
        let checkedTool = null;
        for (let i = 0; i < this.tools.length; i++) {
            const tool = this.tools[i];
            if (tool.IsSelectCheck(x, y)) {
                checkedTool = tool;
                if (tool.CurrentSelection.CurrentState == SelectionState.Selected && controlPressed) {
                    tool.CurrentSelection.ClearSelection();
                    tool.ProcessMoveFinish();
                }
                else {
                    tool.CurrentSelection.CurrentState = SelectionState.Selected;
                    raiseEvent = true;
                }
                break;
            }
        }
        if (checkedTool == null || !controlPressed) {
            for (let i = 0; i < this.tools.length; i++) {
                const tool = this.tools[i];
                if (tool == checkedTool) {
                    continue;
                }
                if (tool.CurrentSelection.CurrentState == SelectionState.Selected) {
                    tool.CurrentSelection.ClearSelection();
                    tool.ProcessMoveFinish();
                    raiseEvent = true;
                }
            }
        }
        if (raiseEvent) {
            this.OnSelectedToolsListChanged();
        }
    }
    ProcessLeftButtonUp(e) {
        if (this.movingTools.length > 0) {
            return this.ProcessStopMoving(e);
        }
        else {
            return false;
        }
    }
    ProcessStopMoving(e) {
        let processed = false;
        for (let i = 0; i < this.movingTools.length; i++) {
            const tool = this.movingTools[i];
            if (tool.CurrentMovement.IsMovingRightNow) {
                tool.ProcessMoveFinish();
                processed = true;
            }
            else {
                tool.ProcessClick(e);
            }
            tool.ProcessDeactivate();
            this.movingTools.splice(i, 1);
        }
        return processed;
    }
    ProcessMovement(e, x, y) {
        for (let i = 0; i < this.movingTools.length; i++) {
            const tool = this.movingTools[i];
            const movement = tool.CurrentMovement;
            let screenX = x;
            let screenY = y;
            const refXY = { screenX, screenY };
            this.screenPoinsConverter.Convert(e.window, refXY);
            screenX = refXY.screenX;
            screenY = refXY.screenY;
            if (!movement.IsMovingRightNow && tool.CheckMinDistanceForStartMoving(x, y)) {
                // тулза не двигалась - начинаем двигать
                tool.ProcessMoveStart(screenX, screenY);
            }
            if (movement.IsMovingRightNow) {
                // тулза двигается - следим за перемещением
                tool.ProcessNewPosition(e.window, screenX, screenY, this.SupportDataConverter && this.chart != null ? this.chart.mainPriceRenderer.Series : null);
                this.chart.IsDirty(LayersEnum.Tools);
            }
        }
    }
    ProcessHoverTool(e, x, y) {
        let result = false;
        for (let i = 0; i < this.tools.length; i++) {
            const tool = this.tools[i];
            if (tool.IsSelect(x, y) && tool.IsSelectCheck(x, y)) {
                result = true;
                this.ProcessMouseEvents(e, tool, x, y);
                this.HoverToolSet(tool);
                // Обрабатываем только первую попавшуюся тулзу, иначе они сбрасывают друг друга (???)
                break;
            }
        }
        if (!result) {
            this.ProcessMouseEvents(e, null, x, y);
            this.HoverToolSet(null);
        }
        return result;
    }
    GetCursor(e) {
        const hoverTool = this.HoverToolGet();
        if (!isNullOrUndefined(hoverTool)) {
            return hoverTool.GetCursor(e);
        }
        return null;
    }
    ProcessMouseEvents(e, tool, x, y) {
        if (this.hoverTool == null && tool == null) {
            // ничего не делаю
        }
        else if (this.hoverTool == null && tool != null) {
            // ховерится новая тулза
            tool.OnMouseEnter(e);
            e.NeedRedraw = LayersEnum.Tools;
        }
        else if (this.hoverTool != null && tool == null) {
            // убирается ховер с тулзы
            this.hoverTool.OnMouseLeave(e);
            e.NeedRedraw = LayersEnum.Tools;
        }
        else if (this.hoverTool == tool) {
            // вожу по ховернутой тулзе
            tool.OnMouseMove(e);
        }
        else {
            // ховер перепрыгнул с тулзы на тулзу
            this.hoverTool.OnMouseLeave(e);
            tool.OnMouseEnter(e);
            e.NeedRedraw = LayersEnum.Tools;
        }
    }
    // Rest of the class members and methods...
    // Context Menu
    GetContextMenu(e, chart) {
        const hoverTool = this.hoverTool;
        if (!hoverTool) {
            return super.GetContextMenu(e, chart);
        }
        // Cancel moving before showing context menu
        this.ProcessStopMoving();
        return [
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.HoverAndSelectedToolSettings, hoverTool)),
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.CloneTool, hoverTool)),
            chart.TerceraChartActionProcessor.CreateContextMenuItem(TerceraChartAction.Create(TerceraChartActionEnum.DeleteHoverAndSelectedTools))
        ];
    }
    ProcessMouseDoubleClick(e) {
        if (this.IsNewToolRendererActive(e)) {
            return false;
        }
        const chart = this.chart;
        const hoverTool = this.hoverTool;
        let processed = false;
        if (chart && hoverTool && !hoverTool.isTradingTool) {
            hoverTool.ProcessDoubleClick(e);
            if (!hoverTool.excludedOuterActionTypes.Contains(ActionType.DoubleClick)) {
                const action = TerceraChartAction.Create(TerceraChartActionEnum.HoverAndSelectedToolSettings, hoverTool);
                chart.TerceraChartActionProcessor.ProcessTerceraChartAction(action);
                processed = true;
            }
        }
        return processed;
    }
    /**
     * Selection of tools by their screenPoints.
     * @param rect The rectangle to select tools.
     */
    SelectToolsByRect(rect) {
        if (!this.tools)
            return;
        let raiseEvent = false;
        for (let i = 0; i < this.tools.length; i++) {
            const tool = this.tools[i];
            tool.CurrentSelection.ClearSelection();
            for (let j = 0; j < tool.screenPoints.length; j++) {
                const screenPoint = tool.screenPoints[j];
                const scrP0 = screenPoint[0];
                const scrP1 = screenPoint[1];
                let containsPoints = false;
                switch (tool.dataCacheTool.SelectionMode) {
                    case SelectionModeEnum.All:
                        containsPoints = rect.Contains(scrP0, scrP1);
                        break;
                    case SelectionModeEnum.X:
                        containsPoints = rect.X < scrP0 && rect.X + rect.Width > scrP0;
                        break;
                    case SelectionModeEnum.Y:
                        containsPoints = rect.Y < scrP1 && rect.Y + rect.Height > scrP1;
                        break;
                }
                if (containsPoints) {
                    tool.CurrentSelection.CurrentState = SelectionState.Selected;
                    raiseEvent = true;
                    break;
                }
            }
        }
        if (raiseEvent) {
            this.OnSelectedToolsListChanged();
        }
    }
    SelectAllTools() {
        const tools = this.tools;
        const len = tools.length;
        for (let i = 0; i < len; i++) {
            const tool = tools[i];
            tool.CurrentSelection.CurrentState = SelectionState.Selected;
        }
        this.OnSelectedToolsListChanged();
    }
    RemoveTool(toolView) {
        if (toolView === null || toolView === void 0 ? void 0 : toolView.dataCacheTool) {
            ToolsCache.RemoveTool(toolView.dataCacheTool);
        }
    }
    FindMinMax(out_minMax, window) {
        let min = Number.MAX_VALUE;
        let max = -Number.MAX_VALUE;
        for (let i = 0; i < this.tools.length; i++) {
            const toolView = this.tools[i];
            if ((toolView === null || toolView === void 0 ? void 0 : toolView.dataCacheTool) != null) {
                const activeConverter = this.chart.cashItemSeriesSettings.ActiveConverter;
                for (let j = 0; j < toolView.dataCacheTool.Points.length; j++) {
                    let value = toolView.dataCacheTool.Points[j][1];
                    if (activeConverter != null) {
                        value = activeConverter.Calculate(value);
                    }
                    if (value > max) {
                        max = value;
                    }
                    if (value < min) {
                        min = value;
                    }
                }
            }
        }
        out_minMax.tMin = min;
        out_minMax.tMax = max;
        return true;
    }
}
